/**
 * JWT token utilities for client-side expiry detection.
 *
 * These are pure functions with zero dependencies — no React, no Zustand.
 * The JWT is decoded (not verified) purely to read the `exp` claim;
 * verification is the backend's responsibility.
 */

/**
 * Decode the payload section of a JWT without verification.
 * Returns null if the token is malformed.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]!
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/**
 * Extract the `exp` claim from a JWT as a Unix timestamp (seconds).
 * Returns null if the token is malformed or has no `exp` claim.
 */
export function getTokenExpiry(token: string): number | null {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return null
  return payload.exp
}

/**
 * Returns milliseconds until the token expires.
 * Returns 0 if already expired, null if unparseable.
 */
export function getTokenTTL(token: string): number | null {
  const exp = getTokenExpiry(token)
  if (exp === null) return null
  const remaining = exp * 1000 - Date.now()
  return Math.max(0, remaining)
}

/**
 * Check if a JWT token is expired (or will expire within `bufferMs`).
 * Malformed/unparseable tokens are treated as expired (safe default).
 */
export function isTokenExpired(token: string, bufferMs = 0): boolean {
  const ttl = getTokenTTL(token)
  if (ttl === null) return true
  return ttl <= bufferMs
}
